import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useCompanySettings } from '../hooks/useCompanySettings';
import ScrollToTop from '../components/ScrollToTop';
import PreLoader from '../components/PreLoader';
import SEO from '../components/SEO';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import { BlogListingSkeleton } from '../components/BlogSkeletons';
import {
  resolveOfficialBaseUrl,
  truncateSeoTitle,
  truncateSeoDescription,
  buildBlogListingBreadcrumbs,
  buildBlogListingBreadcrumbSchema,
} from '../utils/seo';
import {
  fetchPublishedBlogPosts,
  extractBlogCategories,
  blogPostSearchText,
} from '../services/blogCatalog';

const Select = lazy(() => import('react-select'));

function stripHtmlPreview(html, maxLen = 160) {
  const text = String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
}

function Blog() {
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryFromUrl = searchParams.get('category');
  const { 
    companyName,
    settingsReady,
    primaryColor,
    seoMetaTitle,
    seoMetaDescription,
    seoCanonicalUrl,
  } = useCompanySettings();
  const officialBaseUrl = resolveOfficialBaseUrl({ seoCanonicalUrl, companyName });
  const searchTimeoutRef = useRef(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Estados para animações
  const [visibleElements, setVisibleElements] = useState({
    header: false,
    filters: false,
    posts: false
  });

  // Refs para os elementos que serão observados
  const headerRef = useRef(null);
  const filtersRef = useRef(null);
  const postsRef = useRef(null);

  // Estilos customizados para o React Select
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? (primaryColor || 'var(--primary-color, #3B82F6)') : '#cfd4dd',
      borderRadius: '0.5rem',
      minHeight: '46px',
      height: '46px',
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: primaryColor || 'var(--primary-color, #3B82F6)'
      }
    }),
    singleValue: (base, { selectProps }) => ({
      ...base,
      color: selectProps.hasValue ? '#111827' : '#6B7280'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? (primaryColor || 'var(--primary-color, #3B82F6)')
        : state.isFocused 
        ? `${primaryColor || 'var(--primary-color, #3B82F6)'}15`
        : 'white',
      color: state.isSelected ? 'white' : '#111827',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected 
          ? (primaryColor || 'var(--primary-color, #3B82F6)')
          : `${primaryColor || 'var(--primary-color, #3B82F6)'}15`
      }
    }),
    menu: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    indicatorSeparator: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  };

  useEffect(() => {
    setSelectedCategory(categoryFromUrl || '');
  }, [categoryFromUrl]);

  // Controlar animações de entrada baseadas no scroll
  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading) {
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.1, // Elemento deve estar 10% visível
        rootMargin: '0px 0px -50px 0px' // Margem para ativar antes do elemento estar completamente visível
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              setVisibleElements(prev => ({ ...prev, [elementId]: true }));
              // Para de observar o elemento após animar
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      // Observar todos os elementos com data-animate
      const elementsToObserve = [
        headerRef.current,
        filtersRef.current,
        postsRef.current
      ].filter(Boolean);

      elementsToObserve.forEach(element => {
        if (element) {
          observer.observe(element);
        }
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [isPreLoading]);

  // Uma única leitura Firestore; categorias derivadas do mesmo resultado
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const loaded = await fetchPublishedBlogPosts();
        if (cancelled) return;
        setAllPosts(loaded);
        setCategories(extractBlogCategories(loaded));
      } catch (error) {
        console.error('Erro ao carregar posts:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let filtered = allPosts;

    if (selectedCategory) {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }

    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((post) => blogPostSearchText(post).includes(searchLower));
    }

    setPosts(filtered);
  }, [allPosts, selectedCategory, debouncedSearchTerm]);

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = useCallback(() => {
    setIsPreLoading(false);
  }, []);

  // Finaliza o pre-loading quando os posts estiverem carregados
  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    if (settingsReady && !loading) {
      setIsPreLoading(false);
    }
  }, [settingsReady, loading]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Limpa o timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Define um novo timeout de 1 segundo
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 1000);
  };

  const handleCategoryChange = (category) => {
    const next = category || '';
    setSelectedCategory(next);
    if (next) {
      navigate(`/blog?category=${encodeURIComponent(next)}`);
    } else {
      navigate('/blog');
    }
  };

  // Limpa o timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const blogListingSeo = useMemo(() => {
    const name = (companyName || '').trim();
    const brand = name || (settingsReady ? 'Imobiliária' : '');
    const hasFilter = Boolean(selectedCategory || debouncedSearchTerm);
    const title = !brand
      ? 'Carregando...'
      : selectedCategory
        ? truncateSeoTitle(`${selectedCategory} — Blog ${brand}`)
        : truncateSeoTitle(seoMetaTitle || `Blog ${brand}`);
    const description = truncateSeoDescription(
      seoMetaDescription ||
        (brand
          ? (selectedCategory
            ? `Artigos sobre ${selectedCategory} no blog da ${brand}.`
            : `Notícias, dicas e tendências do mercado imobiliário — ${brand}.`)
          : 'Carregando conteúdo do blog...')
    );
    const canonicalUrl = selectedCategory && !debouncedSearchTerm
      ? `${officialBaseUrl}/blog?category=${encodeURIComponent(selectedCategory)}`
      : `${officialBaseUrl}/blog`;
    const robots = hasFilter && debouncedSearchTerm
      ? 'noindex,follow'
      : 'index,follow,max-image-preview:large';

    return { title, description, canonicalUrl, robots };
  }, [companyName, settingsReady, selectedCategory, debouncedSearchTerm, seoMetaTitle, seoMetaDescription, officialBaseUrl]);

  const blogListingSchemas = useMemo(() => {
    const schemas = [buildBlogListingBreadcrumbSchema(selectedCategory || null, officialBaseUrl)];
    if (!debouncedSearchTerm) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: selectedCategory ? `Blog — ${selectedCategory}` : 'Blog',
        url: blogListingSeo.canonicalUrl,
        description: blogListingSeo.description,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: posts.length,
          itemListElement: posts.slice(0, 20).map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${officialBaseUrl}/blog/${p.slug || p.id}`,
            name: p.title,
          })),
        },
      });
    }
    return schemas;
  }, [selectedCategory, officialBaseUrl, debouncedSearchTerm, posts, blogListingSeo]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <SEO
        title={blogListingSeo.title}
        description={blogListingSeo.description}
        keywords="blog, imóveis, mercado imobiliário, dicas, notícias"
        canonicalUrl={blogListingSeo.canonicalUrl}
        robots={blogListingSeo.robots}
        pageType="website"
        includeBlogRss
        includeSiteSchemas
        supplementarySchemas={blogListingSchemas}
      />

      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      {/* Header do Blog */}
      <div 
        ref={headerRef}
        data-animate="header"
        className={`bg-white shadow-sm transition-all duration-1000 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-7xl mx-auto px-4 pt-6 pb-12">
          {/* Breadcrumbs */}
          <div className="mb-8">
            <BreadcrumbNav
              items={buildBlogListingBreadcrumbs(selectedCategory || null)}
              baseUrl={officialBaseUrl}
              skipSchema
            />
          </div>

          <h1 className="text-4xl font-montserrat font-semibold text-gray-900 text-center mb-4">
            Blog {companyName || 'Imobiliária'}
          </h1>
          <p className="text-gray-600 text-center max-w-2xl mx-auto">
            Acompanhe as últimas notícias e tendências do mercado imobiliário
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div 
        ref={filtersRef}
        data-animate="filters"
        className={`bg-white border-b transition-all duration-1000 delay-200 ${visibleElements.filters ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Campo de Busca */}
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Buscar posts por título, tags ou conteúdo..."
                className="w-full h-[46px] px-4 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg 
                  className="h-5 w-5 text-gray-400" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
            </div>

            {/* Filtro de Categorias */}
            <div className="relative w-full sm:w-64">
              <Suspense fallback={<div className="h-[46px] rounded-lg border border-gray-200 bg-gray-50 animate-pulse" />}>
                <Select
                  value={
                    selectedCategory
                      ? { value: selectedCategory, label: selectedCategory }
                      : { value: '__all__', label: 'Todas as categorias' }
                  }
                  onChange={(option) => {
                    const next = option?.value === '__all__' ? '' : (option?.value || '');
                    handleCategoryChange(next);
                  }}
                  options={[
                    { value: '__all__', label: 'Todas as categorias' },
                    ...categories.map(category => ({
                      value: category,
                      label: category
                    }))
                  ]}
                  placeholder="Todas as categorias"
                  isClearable={false}
                  isSearchable={false}
                  styles={customStyles}
                  className="w-full"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Posts */}
      <div 
        ref={postsRef}
        data-animate="posts"
        className={`max-w-7xl mx-auto px-4 py-12 transition-all duration-1000 delay-400 ${
          loading || visibleElements.posts ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {loading ? (
          <BlogListingSkeleton count={8} />
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {posts.map((post, index) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.slug || post.id}`}
                className={`bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-500 group h-[420px] flex flex-col transform ${
                  visibleElements.posts 
                    ? 'opacity-100 translate-y-0 scale-100' 
                    : 'opacity-0 translate-y-8 scale-95'
                }`}
                style={{
                  transitionDelay: visibleElements.posts ? `${index * 100}ms` : '0ms'
                }}
              >
                {/* Container da Imagem */}
                <div className="w-full h-[180px] relative overflow-hidden bg-gray-100">
                  <LazyLoadImage
                    src={post.imageUrl}
                    alt={post.title}
                    className="card-cover-img"
                    effect="blur"
                    wrapperProps={{
                      className: "w-full h-full absolute inset-0"
                    }}
                    placeholder={
                      <div className="w-full h-full bg-gray-200 animate-pulse" />
                    }
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg';
                    }}
                  />
                </div>
                
                {/* Conteúdo */}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-500 text-sm">{post.category}</span>
                  </div>
                  <h2 className="text-lg font-semibold mt-2 mb-3 text-gray-900 line-clamp-2 group-hover:text-blue-500 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-1">
                    {stripHtmlPreview(post.excerpt || post.summary || post.content)}
                  </p>
                  
                  {/* Tags */}
                  {post.tags && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {post.tags.split(',').map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                          >
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-5 text-gray-400" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-500">
                        {post.createdAt?.toDate 
                          ? new Date(post.createdAt.toDate()).toLocaleDateString()
                          : new Date(post.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Nenhum post encontrado.</p>
          </div>
        )}
      </div>
      
      <ScrollToTop />
    </div>
  );
}

export default Blog; 